import type { PublicConfig } from "@aidkitorg/types/lib/config";

// Best-effort sessionStorage mirror of the portal token, so the session survives
// in-app browsers that aggressively clear cookies (matches the subsurvey link-key
// mirror in Util.tsx). The cookie stays primary; this is only a fallback. Both are
// equally JS-readable — the `portal` cookie isn't httpOnly — so this adds no new
// exposure. portalLogout's sessionStorage.clear() tears it down.
const PORTAL_TOKEN_STORAGE_KEY = 'portal';

export function tryGetPortalToken(): string | undefined {
  try { return window.sessionStorage.getItem(PORTAL_TOKEN_STORAGE_KEY) ?? undefined; }
  catch (e) { return undefined; }
}

/** Write the portal token to the sessionStorage mirror. Call alongside setCookie('portal', …). */
export function trySetPortalToken(token: string): void {
  try { window.sessionStorage.setItem(PORTAL_TOKEN_STORAGE_KEY, token); }
  catch (e) { /* sessionStorage blocked */ }
}

/** Remove the portal token's sessionStorage mirror. Call alongside clearing the cookie,
 * or getPortalToken would keep returning the stale mirror. */
export function tryRemovePortalToken(): void {
  try { window.sessionStorage.removeItem(PORTAL_TOKEN_STORAGE_KEY); }
  catch (e) { /* sessionStorage blocked */ }
}

/** Reads the `portal` token, preferring the cookie and falling back to the
 * sessionStorage mirror. Use in callbacks where a hook can't run. */
export function getPortalToken(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const fromCookie = document.cookie.split("; ").find((c) => c.startsWith("portal="))?.split("=")[1];
  return fromCookie ?? tryGetPortalToken();
}

/** Re-reads on every render; trigger your own re-render after login/logout. */
export function usePortalLoggedIn(): boolean {
  return !!getPortalToken();
}

/**
 * Clears all portal session state and redirects the applicant. Used by both the sidebar logout
 * button and the avatar menu so a future fix can't drift between them.
 *
 * - Clears all cookies except `auth_token` (preserves admin testing context).
 * - Clears `sessionStorage` and `localStorage`.
 * - Redirects according to `application.login.logoutRedirectPath`, defaulting to `/o/home`.
 */
export function portalLogout(publicConfig: Partial<PublicConfig>): void {
  document.cookie.split(';').forEach((cookie) => {
    if (cookie.trim().startsWith('auth_token')) return;
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/';
  });
  sessionStorage.clear();
  localStorage.clear();

  const redirect = publicConfig.application?.login?.logoutRedirectPath;
  if (redirect) {
    if (redirect.startsWith('http') || redirect.startsWith('/')) {
      window.location.href = redirect;
    } else if (redirect === 'apply') {
      window.location.href = '/apply';
    } else {
      window.location.href = '/p/' + redirect;
    }
  } else {
    window.location.href = '/o/home';
  }
}
